import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../components/Icon';
import Botao from '@/components/Botao';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Modal from '../../components/layout/Modal';
import CampoSeletor, { CampoSeletorOptions } from '../../components/Formulario/CampoSeletor';
import {
    mdiPhone,
    mdiPhoneHangup,
    mdiMicrophone,
    mdiMicrophoneOff,
    mdiWindowMinimize,
    mdiWindowMaximize,
    mdiVolumeHigh,
    mdiVolumeMute,
    mdiPhoneDial,
    mdiThumbUp,
    mdiThumbDown,
    mdiAccountSwitch,
} from '@mdi/js';
import { LigacaoInfo, LigacaoState } from '@/hooks/useLigacaoLiveKit';
import axios from 'axios';
import { useQuery } from 'react-query';
import { TrocarAtendente } from './TrocarAtendente';
import { useAuth } from '@/lib/useAuth';

interface LigacaoProps {
    ligacao: LigacaoInfo | null;
    ligacaoState: LigacaoState | null;
    clienteNome: string;
    clienteImagem?: string;
    onAtender: () => void;
    onEncerrar: (solucionado?: boolean) => void;
    onTransferir: (targetUserId?: number, targetDepartmentId?: number, targetUserGroupId?: number) => void;
    onMinimizar: () => void;
    isMinimizado: boolean;
    tempoInicio?: Date;
    isMuted: boolean;
    isSpeakerOn: boolean;
    onToggleMute: () => void;
    onToggleSpeaker: () => void;
    nivelAudioLocal?: number;   // Nível do áudio local (0-100)
    nivelAudioRemoto?: number;  // Nível do áudio remoto (0-100)
}

const Ligacao: React.FC<LigacaoProps> = ({
    ligacao,
    ligacaoState,
    clienteNome,
    clienteImagem,
    onAtender,
    onEncerrar,
    onTransferir,
    onMinimizar,
    isMinimizado,
    tempoInicio,
    isMuted,
    isSpeakerOn,
    onToggleMute,
    onToggleSpeaker,
    nivelAudioLocal = 0,
    nivelAudioRemoto = 0,
}) => {
    const [tempoDecorrido, setTempoDecorrido] = useState(0);
    const [showFinalizeOptions, setShowFinalizeOptions] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [atendenteSelecionado, setAtendenteSelecionado] = useState<string | null>(null);
    const [grupoDoUsuarioSelecionado, setGrupoDoUsuarioSelecionado] = useState<string | null>(null);
    const [departamentoSelecionado, setDepartamentoSelecionado] = useState<string | null>(null);
    const [atendSearch, setAtendSearch] = useState('');

    const { user } = useAuth();

    // Queries para buscar usuários e grupos (usando endpoints /box que são acessíveis a todos)
    const queryUsuarios = useQuery(['usuario-box'], async () => {
        const { data } = await axios.get('/api/usuario/box');
        return data?.result;
    });

    const queryGrupos = useQuery(['usuario_grupo-box'], async () => {
        const { data } = await axios.get('/api/usuario_grupo/box');
        return data?.result;
    });

    // Preparar opções para o seletor
    const atendentes = React.useMemo(() => {
        if (!queryUsuarios.data) return [];
        // Endpoint /box já retorna apenas usuários ativos que podem receber transferências
        return queryUsuarios.data
            .filter((usuario: any) => usuario.id !== user?.id) // Não mostrar o próprio usuário
            .map((usuario: any) => ({
                id: `user_${usuario.id}`,
                label: usuario.nome,
                group: 'Atendentes',
                groupId: 'atendentes'
            }));
    }, [queryUsuarios.data, user?.id]);

    const departamentos = React.useMemo(() => {
        if (!queryGrupos.data) return [];
        return queryGrupos.data.map((grupo: any) => ({
            id: `dept_${grupo.id}`,
            label: grupo.nome,
            group: grupo.nome,
            groupId: `dept_${grupo.id}`
        }));
    }, [queryGrupos.data]);

    // Combinar todas as opções - atendentes ficam no grupo "Atendentes", departamentos são grupos próprios
    const todasOpcoes = React.useMemo(() => {
        const opcoes = [...atendentes, ...departamentos];
        return opcoes;
    }, [atendentes, departamentos]);

    const handleTransferConfirm = () => {
        if (atendenteSelecionado) {
            // Extrair o ID real removendo o prefixo "user_"
            const userId = parseInt(atendenteSelecionado.replace('user_', ''));
            const grupoId = grupoDoUsuarioSelecionado ? parseInt(grupoDoUsuarioSelecionado) : undefined;
            onTransferir(userId, undefined, grupoId);
        } else if (departamentoSelecionado) {
            // Extrair o ID real removendo o prefixo "dept_"
            const deptId = parseInt(departamentoSelecionado.replace('dept_', ''));
            onTransferir(undefined, deptId);
        }
        setShowTransferModal(false);
        setAtendenteSelecionado(null);
        setGrupoDoUsuarioSelecionado(null);
        setDepartamentoSelecionado(null);
    };

    const handleCloseTransferModal = () => {
        setShowTransferModal(false);
        setAtendenteSelecionado(null);
        setGrupoDoUsuarioSelecionado(null);
        setDepartamentoSelecionado(null);
    };

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (tempoInicio) {
            interval = setInterval(() => {
                const segundosDecorridos = Math.floor((new Date().getTime() - tempoInicio.getTime()) / 1000);
                setTempoDecorrido(segundosDecorridos);
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [tempoInicio]);

    const formatarTempo = (segundos: number): string => {
        const minutos = Math.floor(segundos / 60);
        const segundosRestantes = segundos % 60;
        return `${minutos.toString().padStart(2, '0')}:${segundosRestantes.toString().padStart(2, '0')}`;
    };

    const [position, setPosition] = useState({ x: 20, y: 20 });
    const [isDragging, setIsDragging] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement | null>(null);

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (containerRef.current && !isMinimizado) {
            setIsDragging(true);
            setStartPos({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
            e.preventDefault();
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (isDragging && containerRef.current) {
            const maxX = window.innerWidth - (containerRef.current?.offsetWidth || 0);
            const maxY = window.innerHeight - (containerRef.current?.offsetHeight || 0);

            const newX = Math.min(Math.max(0, e.clientX - startPos.x), maxX);
            const newY = Math.min(Math.max(0, e.clientY - startPos.y), maxY);

            setPosition({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]);

    if (isMinimizado) {
        return (
            <div
                className="ligacao-minimizada"
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    backgroundColor: 'var(--tc-color-primary)',
                    borderRadius: '50%',
                    width: '60px',
                    height: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    zIndex: 1000,
                }}
                onClick={onMinimizar}
            >
                <Icon path={mdiPhone} size={1.5} color="white" />
                <div
                    style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        backgroundColor: 'var(--tc-color-danger)',
                        borderRadius: '50%',
                        width: '15px',
                        height: '15px',
                    }}
                />
                {ligacaoState?.status === 'em_andamento' && (
                    <>
                        {nivelAudioLocal > 10 && !isMuted && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '12px',
                                    left: '5px',
                                    backgroundColor: 'var(--tc-color-primary-dark)',
                                    borderRadius: '50%',
                                    width: '8px',
                                    height: '8px',
                                    boxShadow: `0 0 ${Math.min(8, nivelAudioLocal / 12)}px 2px var(--tc-color-primary-light)`,
                                }}
                            />
                        )}
                        {nivelAudioRemoto > 10 && isSpeakerOn && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '12px',
                                    right: '5px',
                                    backgroundColor: 'var(--tc-color-success)',
                                    borderRadius: '50%',
                                    width: '8px',
                                    height: '8px',
                                    boxShadow: `0 0 ${Math.min(8, nivelAudioRemoto / 12)}px 2px var(--tc-color-success-light)`,
                                }}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="ligacao-container"
            style={{
                position: 'fixed',
                left: `${position.x}px`,
                top: `${position.y}px`,
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                width: '280px',
                zIndex: 1000,
            }}
        >
            <div
                className="ligacao-header"
                style={{
                    backgroundColor: ligacaoState?.status === 'finalizado' ? 'var(--tc-color-gray-700)' : 'var(--tc-color-primary)',
                    color: 'white',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px',
                    padding: '12px',
                    cursor: 'move',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
                onMouseDown={handleMouseDown}
            >
                {ligacaoState?.status === 'chamando' ? (
                    <span>Chamando</span>
                ) : ligacaoState?.status === 'finalizado' ? (
                    <span>Ligação finalizada</span>
                ) : (
                    <span>Ligação em andamento</span>
                )}
                <Botao
                    variant="none-secondary"
                    icon={isMinimizado ? mdiWindowMaximize : mdiWindowMinimize}
                    onClick={onMinimizar}
                />
            </div>

            <VBox center style={{ padding: '20px' }}>
                {clienteImagem ? (
                    <img
                        src={clienteImagem}
                        alt={clienteNome}
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            marginBottom: '10px',
                            border: '2px solid var(--tc-color-primary)',
                        }}
                    />
                ) : (
                    <div
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: 'var(--tc-color-gray-200)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '10px',
                            fontSize: '32px',
                            color: 'var(--tc-color-primary)',
                            fontWeight: 'bold',
                        }}
                    >
                        {clienteNome.charAt(0).toUpperCase()}
                    </div>
                )}

                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                    {clienteNome}
                </div>

                {(
                    showFinalizeOptions && (
                        <div>
                            <p style={{ textAlign: "center" }}>O problema do cliente foi resolvido?</p>

                            <VBox>
                                <Botao
                                    variant="success"
                                    onClick={() => { onEncerrar(true); setShowFinalizeOptions(false); }}
                                    icon={mdiThumbUp}
                                >
                                    Sim, solucionado
                                </Botao>
                                <Botao
                                    variant="secondary"
                                    onClick={() => { onEncerrar(false); setShowFinalizeOptions(false); }}
                                    icon={mdiThumbDown}
                                >
                                    Não resolvido
                                </Botao>
                            </VBox>

                        </div>
                    )
                )}

                {(!showFinalizeOptions && (
                    <div>
                        <div style={{ color: 'var(--tc-color-gray-500)', marginBottom: '20px' }}>
                            {formatarTempo(tempoDecorrido)}
                        </div>

                        {/* Indicadores de áudio */}
                        <div style={{ width: '100%', marginBottom: '15px' }}>
                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                                <span style={{ fontSize: '14px', color: 'var(--tc-color-gray-600)' }}>
                                    <Icon
                                        path={isMuted ? mdiMicrophoneOff : mdiMicrophone}
                                        size={0.8}
                                        color={isMuted ? 'var(--tc-color-gray-500)' : 'var(--tc-color-primary)'}
                                        style={{ marginRight: '4px' }}
                                    />
                                    {/* {Math.round((ligacaoState?.localAudioLevel || 0) * 100)}% */}
                                </span>
                            </div>
                            <div style={{ display: 'flex', marginBottom: '8px' }}>
                                <div style={{ flex: 1, backgroundColor: 'var(--tc-color-gray-200)', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            width: `${isMuted ? 0 : Math.max(1, (ligacaoState?.localAudioLevel || 0) * 100)}%`,
                                            height: '100%',
                                            backgroundColor: 'var(--tc-color-primary)',
                                            transition: 'width 0.15s ease-in-out'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <HBox style={{ marginBottom: '20px', gap: '16px', display: 'flex', justifyContent: 'center' }}>
                            <Botao
                                variant={isMuted ? "danger" : "none-primary"}
                                icon={isMuted ? mdiMicrophoneOff : mdiMicrophone}
                                onClick={onToggleMute}
                                rounded
                                title={isMuted ? "Ativar microfone" : "Desativar microfone"}
                            />

                            {ligacaoState?.status === 'em_andamento' && (
                                <Botao
                                    variant="outline-secondary"
                                    icon={mdiAccountSwitch}
                                    onClick={() => {
                                        setShowTransferModal(true);
                                    }}
                                    rounded
                                    title="Transferir chamada"
                                />
                            )}

                            {ligacaoState?.status === 'chamando' ? (
                                <Botao
                                    variant="primary"
                                    icon={mdiPhoneDial}
                                    onClick={() => onAtender()}
                                    rounded
                                    title="Atender"
                                />
                            ) : (
                                <Botao
                                    variant="danger"
                                    icon={mdiPhoneHangup}
                                    onClick={() => {
                                        // Encerra imediatamente
                                        onEncerrar();
                                        // Abre opções de solução logo em seguida
                                        setShowFinalizeOptions(true);
                                    }}
                                    rounded
                                    title="Encerrar ligação"
                                />
                            )}
                        </HBox>
                    </div>
                ))}

            </VBox>

            {/* Modal de transferência */}
            <Modal
                show={showTransferModal}
                onClose={handleCloseTransferModal}
                onCancel={handleCloseTransferModal}
                onConfirm={handleTransferConfirm}
                title="Transferir chamada"
                confirmProps={{
                    disabled: !atendenteSelecionado && !departamentoSelecionado,
                    label: 'Transferir',
                }}
            >
                <TrocarAtendente
                    currentUserId={user.id || 0}
                    currentUserName={user.nome || ''}
                    selectedUserId={atendenteSelecionado}
                    selectedGroupId={departamentoSelecionado}
                    selectedUserGroupId={grupoDoUsuarioSelecionado}
                    onSelectedUserChange={(userId, userGroupId) => {
                        setAtendenteSelecionado(userId);
                        setGrupoDoUsuarioSelecionado(userGroupId ?? null);
                    }}
                    onSelectedGroupChange={setDepartamentoSelecionado}
                />
            </Modal>
        </div>
    );
};

export default Ligacao;
